<!-- src/components/TalkNowRecord.vue -->
<template>
  <div class="talk-now-container">
    <!-- Audio Player Section with Plyr -->
    <div class="audio-section">
      <audio
        ref="audioPlayer"
        :src="record.recordingUrl"
        preload="metadata"
        @play="isPlaying = true"
        @pause="isPlaying = false"
        @timeupdate="onTimeUpdate"
      >
        Your browser does not support the audio element.
      </audio>
    </div>

    <!-- Notes Section -->
    <div class="notes-section">
      <div class="notes-header" @click="showNotes = !showNotes">
        <h3>
          Audio Annotations
          <span v-if="notes.length > 0" class="notes-count">({{ notes.length }})</span>
        </h3>
        <span class="toggle-arrow" :class="{ 'arrow-expanded': showNotes }">
          ▼
        </span>
      </div>
      
      <div v-if="showNotes" class="notes-content">
        <button 
          class="add-note-btn"
          @click.stop="addNoteAtCurrentTime"
          :disabled="!isPlaying && currentTime === 0"
        >
          Add Note
        </button>
        
        <div class="notes-timeline" v-if="notes.length > 0">
          <div 
            v-for="note in sortedNotes" 
            :key="note.id"
            class="note-marker"
            :style="{ left: getMarkerPosition(note.timestamp) + '%' }"
            @click.stop="seekToTime(note.timestamp)"
            :title="`${formatDuration(note.timestamp)}: ${note.text.substring(0, 50)}...`"
          ></div>
        </div>

        <div class="notes-list">
          <div 
            v-for="note in sortedNotes" 
            :key="note.id"
            class="note-item"
            :class="{ 'note-active': isNoteActive(note.timestamp) }"
          >
            <div class="note-timestamp" @click.stop="seekToTime(note.timestamp)">
              {{ formatDuration(note.timestamp) }}
            </div>
            <div class="note-content">
              <textarea 
                v-model="note.text"
                @blur="saveNote()"
                @click.stop
                placeholder="Add your note here..."
                rows="2"
              ></textarea>
              <button class="delete-note" @click.stop="deleteNote(note.id)">×</button>
            </div>
          </div>
          <div v-if="notes.length === 0" class="no-notes">
            No notes yet. Click "Add Note" while listening to create timestamped annotations.
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Stats -->
    <div class="stats-grid">
      <div class="stat-box">
        <div class="stat-label">Agent</div>
        <div class="stat-value">{{ record.agent }}</div>
      </div>
      <div class="stat-box">
        <div class="stat-label">Timestamp</div>
        <div class="stat-value">{{ formatTimestamp(record.timestamp) }}</div>
      </div>
    </div>

    <!-- Transcript Section -->
    <div class="transcript-section">
      <div class="transcript-header">
        <h3>Transcript</h3>
        <button 
          class="toggle-transcript"
          @click="showFullTranscript = !showFullTranscript"
        >
          {{ showFullTranscript ? 'Show Less' : 'Show Full Transcript' }}
        </button>
      </div>
      <div 
        class="transcript-content"
        :class="{ 'transcript-collapsed': !showFullTranscript }"
        v-html="record.talkNowTranscript"
      ></div>
    </div>

    <!-- Summary & Opinion -->
    <div class="summary-opinion-grid">
      <div class="summary-box">
        <h3>Summary</h3>
        <p>{{ record.talkNowSummary }}</p>
      </div>
      <div class="opinion-box">
        <h3>Opinion</h3>
        <p>{{ record.talkNowOpinion }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Plyr from 'plyr'
import 'plyr/dist/plyr.css'

export default {
  name: 'TalkNowRecord',
  
  props: {
    record: {
      type: Object,
      required: true
    },
    parentCallSid: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isPlaying: false,
      showFullTranscript: false,
      showNotes: false,
      currentTime: 0,
      notes: [],
      nextNoteId: 1,
      player: null
    }
  },

  computed: {
    sortedNotes() {
      return [...this.notes].sort((a, b) => a.timestamp - b.timestamp)
    }
  },

  mounted() {
    // Load saved notes from localStorage
    this.loadNotes()
    
    // Initialize Plyr
    this.initializePlyr()
  },

  beforeUnmount() {
    if (this.player) {
      this.player.destroy()
    }
  },

  methods: {
    formatDuration(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.floor(seconds % 60)
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },

    formatTimestamp(timestamp) {
      return new Date(timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    initializePlyr() {
      const plyrOptions = {
        controls: [
          'play-large',
          'play',
          'progress', 
          'current-time',
          'duration',
          'mute',
          'volume',
          'settings',
          'download'
        ],
        settings: ['speed'],
        speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
        download: {
          enabled: true,
          url: this.record?.recordingUrl
        },
        keyboard: { 
          focused: true, 
          global: false 
        },
        seekTime: 10
      }

      this.player = new Plyr(this.$refs.audioPlayer, plyrOptions)
      
      // Add event listeners
      this.player.on('play', () => {
        this.isPlaying = true
      })
      
      this.player.on('pause', () => {
        this.isPlaying = false
      })
      
      this.player.on('timeupdate', () => {
        this.currentTime = this.player.currentTime
      })

      // Customize download button after Plyr initializes
      this.$nextTick(() => {
        this.customizeDownloadButton()
      })
    },

    customizeDownloadButton() {
      const downloadBtn = this.$el.querySelector('[data-plyr="download"]')
      if (downloadBtn) {
        // Replace icon with text
        downloadBtn.innerHTML = '<span style="font-size: 12px;">Right-Click Download</span>'
        
        // Disable left-click
        downloadBtn.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
        })
      }
    },

    onTimeUpdate() {
      // This method is now handled by the Plyr timeupdate event
      this.currentTime = this.player?.currentTime || 0
    },

    addNoteAtCurrentTime() {
      const timestamp = this.currentTime || 0
      const newNote = {
        id: this.nextNoteId++,
        timestamp: timestamp,
        text: ''
      }
      this.notes.push(newNote)
      this.saveNotes()
      
      // Focus the new note's textarea
      this.$nextTick(() => {
        const textareas = this.$el.querySelectorAll('.note-item textarea')
        const lastTextarea = textareas[textareas.length - 1]
        if (lastTextarea) lastTextarea.focus()
      })
    },

    seekToTime(timestamp) {
      if (this.player) {
        this.player.currentTime = timestamp
      }
    },

    deleteNote(noteId) {
      this.notes = this.notes.filter(note => note.id !== noteId)
      this.saveNotes()
    },

    saveNote() {
      this.saveNotes()
    },

    getMarkerPosition(timestamp) {
      if (!this.record.duration) return 0
      return (timestamp / this.record.duration) * 100
    },

    isNoteActive(timestamp) {
      // Highlight note if current time is within 2 seconds
      return Math.abs(this.currentTime - timestamp) < 2
    },

    async saveNotes() {
      const notesJson = JSON.stringify(this.notes);
      
      try {
        // Save to database
        await axios.put(
          `/portaldb/db/updateTalkNowAudioAnnotations/${this.parentCallSid}/${this.record.recordId}`,
          { audioAnnotations: notesJson }
        );
      } catch (error) {
        console.error('Failed to save audio annotations to database:', error);
      }
      
      // Also save to localStorage as backup
      const notesKey = `notes_${this.record.recordId}`;
      localStorage.setItem(notesKey, notesJson);
    },

    loadNotes() {
      let notesJson = null;
      
      // First try to load from database (the audioAnnotations property)
      if (this.record.audioAnnotations) {
        notesJson = this.record.audioAnnotations;
      } else {
        // Fallback to localStorage
        const notesKey = `notes_${this.record.recordId}`;
        notesJson = localStorage.getItem(notesKey);
      }
      
      if (notesJson) {
        try {
          this.notes = JSON.parse(notesJson);
          this.nextNoteId = Math.max(...this.notes.map(n => n.id), 0) + 1;
        } catch (error) {
          console.error('Error parsing audio annotations:', error);
          this.notes = [];
          this.nextNoteId = 1;
        }
      }
    },

    toggleNotes() {
      this.showNotes = !this.showNotes
      console.log('Toggle notes:', this.showNotes) // Debug
    }
  }
}
</script>

<style scoped>
.talk-now-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background-color: white;
  border-radius: 0.5rem;
}

.audio-section {
  background-color: #f8f8f0;
  padding: 0.75rem;
  border-radius: 0.5rem;
  --plyr-color-main: #8b4513;
}

/* Custom styling for download button text */
:deep([data-plyr="download"]) {
  width: 85px !important;
  text-align: center !important;
  background-color: #8b4513 !important;
  line-height: 1.15 !important;
  color: #ffffff !important;
}

:deep([data-plyr="download"] span) {
  pointer-events: none;
}

.duration {
  font-size: 0.875rem;
  color: #666;
  margin-top: 0.5rem;
  display: none;
}

/* Notes Section */
.notes-section {
  border: 1px solid #d2b48c;
  border-radius: 0.5rem;
  padding: 1rem;
  padding-bottom: .25rem;
}

.notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin-bottom: 1rem;
}

.notes-header h3 {
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.notes-count {
  font-size: 0.875rem;
  color: #666;
  font-weight: normal;
}

.toggle-arrow {
  font-size: 1rem;
  color: #8b4513;
  transition: transform 0.3s ease;
  user-select: none;
}

.toggle-arrow.arrow-expanded {
  transform: rotate(180deg);
}

.notes-content {
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.add-note-btn {
  background-color: #8b4513;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.add-note-btn:hover:not(:disabled) {
  background-color: #6b3410;
}

.add-note-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.notes-timeline {
  position: relative;
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 10px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.note-marker {
  position: absolute;
  top: 0;
  width: 4px;
  height: 100%;
  background-color: #8b4513;
  cursor: pointer;
  border-radius: 2px;
}

.note-marker:hover {
  background-color: #6b3410;
  width: 6px;
}

.notes-list {
  /* Removed max-height to prevent scrollbar */
}

.note-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
  transition: background-color 0.2s;
}

.note-item.note-active {
  background-color: #fff5ee;
  border-color: #d2b48c;
}

.note-timestamp {
  flex-shrink: 0;
  font-weight: 500;
  color: #8b4513;
  cursor: pointer;
  min-width: 60px;
  font-family: monospace;
}

.note-timestamp:hover {
  text-decoration: underline;
}

.note-content {
  flex: 1;
  display: flex;
  gap: 0.5rem;
}

.note-content textarea {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  padding: 0.5rem;
  font-family: inherit;
  resize: vertical;
  min-height: 50px;
}

.note-content textarea:focus {
  outline: none;
  border-color: #8b4513;
}

.delete-note {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.25rem;
  line-height: 1;
  flex-shrink: 0;
}

.delete-note:hover {
  color: #d32f2f;
}

.no-notes {
  text-align: center;
  color: #666;
  font-style: italic;
  padding: 2rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.stat-box {
  padding: 0.75rem;
  background-color: #f8f8f0;
  border-radius: 0.5rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #666;
}

.stat-value {
  font-weight: 500;
}

.transcript-section {
  border: 1px solid #d2b48c;
  border-radius: 0.5rem;
  padding: 1rem;
}

.transcript-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.transcript-header h3 {
  font-weight: 500;
}

.toggle-transcript {
  color: #8b4513;
  font-size: 0.875rem;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
}

.toggle-transcript:hover {
  text-decoration: underline;
}

.transcript-content {
  line-height: 1.5;
}

.transcript-collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.summary-opinion-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 768px) {
  .summary-opinion-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.summary-box,
.opinion-box {
  border: 1px solid #d2b48c;
  border-radius: 0.5rem;
  padding: 1rem;
}

.summary-box h3,
.opinion-box h3 {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

:deep(.agent-label) {
  color: #8b4513;
  font-weight: 500;
}

:deep(.detainee-label) {
  color: #8b4513;
  font-weight: 500;
}

/* Remove focus outlines from all Plyr elements */
:deep(.plyr *:focus) {
  outline: none !important;
  box-shadow: none !important;
}

:deep(.plyr__control:focus) {
  outline: none !important;
  box-shadow: none !important;
}

:deep(.plyr__progress:focus) {
  outline: none !important;
  box-shadow: none !important;
}

:deep(.plyr__volume:focus) {
  outline: none !important;
  box-shadow: none !important;
}

@media (max-width: 767px) {
  .audio-section {
    padding: 0.5rem;
  }
}
</style>
